import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatINR, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PaymentBadge } from "@/components/badges";

const MODES = ["Cash", "UPI", "Card", "Cheque"];

export default function PaymentCollectionModal({ delivery, open, onClose }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState("Cash");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [bank, setBank] = useState("");
  const [card4, setCard4] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [remarks, setRemarks] = useState("");

  const outstanding = delivery ? delivery.outstanding : 0;

  const submit = useMutation({
    mutationFn: async () =>
      api.post("/payments", {
        delivery_id: delivery.id,
        mode,
        amount: parseFloat(amount),
        reference,
        bank,
        card_last4: card4,
        due_date: dueDate,
        remarks,
      }),
    onSuccess: () => {
      toast.success("Payment recorded");
      qc.invalidateQueries({ queryKey: ["deliveries"] });
      qc.invalidateQueries({ queryKey: ["delivery-detail", delivery.id] });
      qc.invalidateQueries({ queryKey: ["payments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      reset();
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const reset = () => {
    setMode("Cash");
    setAmount("");
    setReference("");
    setBank("");
    setCard4("");
    setDueDate("");
    setRemarks("");
  };

  if (!delivery) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="payment-sheet">
        <SheetHeader>
          <SheetTitle>Collect Payment</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-5">
          <div className="rounded-lg border bg-secondary/50 p-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Customer</span>
              <span className="font-medium">{delivery.customer_name}</span>
            </div>
            <div className="mt-1 flex justify-between text-sm">
              <span className="text-muted-foreground">Total</span>
              <span className="font-mono">{formatINR(delivery.total_amount)}</span>
            </div>
            <div className="mt-1 flex justify-between text-sm">
              <span className="text-muted-foreground">Outstanding</span>
              <span className="font-mono font-semibold text-rose-600 dark:text-rose-400">{formatINR(outstanding)}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Payment Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODES.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="pt-1"><PaymentBadge mode={mode} /></div>
          </div>

          <div className="space-y-2">
            <Label>Amount (₹)</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={outstanding.toString()}
              data-testid="payment-amount-input"
            />
            <button className="text-xs text-primary hover:underline" onClick={() => setAmount(String(outstanding))}>
              Fill outstanding ({formatINR(outstanding)})
            </button>
          </div>

          {mode === "UPI" && (
            <div className="space-y-2">
              <Label>UPI Reference / Txn ID</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="payment-upi-ref" />
            </div>
          )}
          {mode === "Card" && (
            <div className="space-y-2">
              <Label>Card Last 4 Digits</Label>
              <Input maxLength={4} value={card4} onChange={(e) => setCard4(e.target.value)} data-testid="payment-card4" />
            </div>
          )}
          {mode === "Cheque" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Cheque No.</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="payment-cheque-no" />
              </div>
              <div className="space-y-2">
                <Label>Bank</Label>
                <Input value={bank} onChange={(e) => setBank(e.target.value)} data-testid="payment-cheque-bank" />
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label>Remarks (optional)</Label>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="payment-remarks" />
          </div>
        </div>
        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => submit.mutate()}
            disabled={!amount || parseFloat(amount) <= 0 || submit.isPending}
            data-testid="payment-submit-button"
          >
            Record Payment
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
